import React, { useContext, useRef, useState } from 'react';
import CursorContext from '../../providers/Cursor';
import PillLink from '../../ui/cinematic/PillLink';
import ReCAPTCHA from 'react-google-recaptcha';
import { stegaClean } from '@sanity/client/stega';
import AceptaPolitica from '../../ui/cinematic/AceptaPolitica';
import { PROPS_TRAMPA, enviarContacto } from '../../../cms/contactos';
import { eventoGTM } from '../../../cms/cookies';
import { elemento, useTextos } from '../../../cms/textos';
import RESPALDO from '../../../Data/Paginas/paginaContacto.json';

// Tarjeta oscura con campos rellenos: se usa igual en la home y en /contact
const labelCls = 'text-sm text-cream/60';
const fieldCls = 'w-full rounded-2xl bg-cream/[0.06] px-5 py-4 text-lg text-cream ring-1 ring-inset ring-cream/10 transition duration-300 placeholder:text-cream/30 hover:ring-cream/25 focus:bg-cream/10 focus:outline-none focus:ring-cream/50';

/**
 * Los textos salen de "Páginas → Contacto → Formulario" y los lee el formulario
 * mismo: así sirven igual en la home y en /contact. Los mensajes de error quedan
 * fijos acá.
 */
const Formulario = () => {
    const textos = useTextos('paginaContacto', RESPALDO).formulario;
    const Exito = elemento(textos, 'exito', 'p');

    const refCaptcha = useRef(null);
    const [statusCaptcha, setStatusCaptcha] = useState(null)
    const [trampa, setTrampa] = useState('');
    const [acepta, setAcepta] = useState(false);
    const [errorAcepta, setErrorAcepta] = useState(null);
    const { selectEnter, selectExit } = useContext(CursorContext);
    const [formData, setFormData] = useState({
        nombre: "",
        email: "",
        telefono: "",
        mensaje: "",
        empresa: "",
        area: "",
    });
    const [status, setStatus] = useState({
        loading: false,
        error: null,
        success: false,
    });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        // Verificar que todos los campos requeridos estén llenos
        const { nombre, email, mensaje, empresa, telefono } = formData;
        if (!nombre || !email || !mensaje || !empresa || !telefono) {
            setStatus({
                loading: false,
                error: "Por favor, completa todos los campos.",
                success: false,
            });
            return;
        }

        if (!acepta) {
            setErrorAcepta("Para enviar, acepta la Política de Privacidad.");
            setStatus({ loading: false, error: null, success: false });
            return;
        }

        if (!statusCaptcha) {
            setStatus({
                loading: false,
                error: "Por favor, completa el captcha.",
                success: false,
            });
            return;
        }

        // Validar formato de email
        if (!validateEmail(email)) {
            setStatus({
                loading: false,
                error: "Por favor, ingresa un email válido.",
                success: false,
            });
            return;
        }

        if (telefono.length > 12) {
            setStatus({
                loading: false,
                error: "Por favor, ingresa un número de teléfono válido. Ejemplo: +569 1234 5678",
                success: false,
            });
            return;
        }


        setStatus({ loading: true, error: null, success: false });
        try {
            await enviarContacto('contacto', { nombre, email, telefono, empresa, mensaje, interes: formData.area, aceptaPolitica: acepta }, trampa);

            // El evento de GTM va después del envío: así la conversión cuenta solo los que
            // llegaron. Sin los datos de la persona: ninguna etiqueta los usa y quedarían al
            // alcance de los scripts de terceros que corren en la página.
            eventoGTM({ event: 'formSubmit' });
            setStatus({ loading: false, error: null, success: true });
            setFormData({ nombre: "", email: "", mensaje: "", empresa: "", telefono: "", area: "" });
            setAcepta(false);
            refCaptcha.current?.reset();
            setStatusCaptcha(null);
        } catch (err) {
            setStatus({
                loading: false,
                error: err.message || "Error al enviar el mensaje",
                success: false,
            });
        }
    };

    // Los ejemplos van en un atributo: ahí los caracteres invisibles de la vista
    // previa del panel (stega) no dibujan recuadro y solo estorbarían
    const fields = [
        { id: 'nombre', type: 'text', autoComplete: 'name' },
        { id: 'empresa', type: 'text', autoComplete: 'organization' },
        { id: 'telefono', type: 'tel', autoComplete: 'tel' },
        { id: 'email', type: 'email', autoComplete: 'email' },
    ].map((f) => ({ ...f, label: textos[f.id], placeholder: stegaClean(textos[`${f.id}Ejemplo`]) }));

    return (
        // noValidate: la validación la hace handleSubmit con sus propios mensajes
        <form
            id="formcontacto"
            noValidate
            onSubmit={handleSubmit}
            className="relative w-full rounded-[2rem] bg-ink p-6 text-cream shadow-[0_40px_80px_-24px_rgba(10,10,10,0.45)] sm:p-8 md:p-12"
        >
            <input {...PROPS_TRAMPA} value={trampa} onChange={(e) => setTrampa(e.target.value)} />
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                {fields.map(({ id, label, type, placeholder, autoComplete }) => (
                    <div key={id} className="flex flex-col gap-2">
                        <label htmlFor={id} className={labelCls}>
                            {label} <span aria-hidden="true" className="text-cream/40">*</span>
                        </label>
                        <input
                            onMouseEnter={selectEnter} onMouseLeave={selectExit}
                            className={fieldCls}
                            type={type}
                            placeholder={placeholder}
                            autoComplete={autoComplete}
                            id={id}
                            name={id}
                            value={formData[id]}
                            onChange={handleChange}
                            required
                        />
                    </div>
                ))}
            </div>

            {/* Área como píldoras (radios) en vez del dropdown */}
            <fieldset className="mt-8">
                <legend className={labelCls}>{textos.pregunta}</legend>
                <div className="mt-3 flex flex-wrap gap-2">
                    {textos.areas.map((area, i) => (
                        // El valor viaja a la Bandeja de contactos: sin la marca de la vista previa
                        <label key={i} onMouseEnter={selectEnter} onMouseLeave={selectExit} className="cursor-pointer">
                            <input
                                type="radio"
                                name="area"
                                value={stegaClean(area)}
                                checked={formData.area === stegaClean(area)}
                                onChange={handleChange}
                                className="peer sr-only"
                            />
                            <span className="block rounded-full px-4 py-2 text-sm text-cream/70 ring-1 ring-inset ring-cream/20 transition-colors duration-300 hover:text-cream hover:ring-cream/50 peer-checked:bg-cream peer-checked:text-ink peer-checked:ring-cream peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-cream">
                                {area}
                            </span>
                        </label>
                    ))}
                </div>
            </fieldset>

            <div className="mt-8 flex flex-col gap-2">
                <label htmlFor="mensaje" className={labelCls}>
                    {textos.mensaje} <span aria-hidden="true" className="text-cream/40">*</span>
                </label>
                <textarea
                    onMouseEnter={selectEnter} onMouseLeave={selectExit}
                    className={`${fieldCls} resize-none`}
                    rows={5}
                    placeholder={stegaClean(textos.mensajeEjemplo)}
                    id="mensaje"
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    required
                />
            </div>

            <AceptaPolitica
                className="mt-8"
                texto={textos.acepto}
                enlace={textos.aceptoEnlace}
                marcada={acepta}
                onChange={(valor) => { setAcepta(valor); setErrorAcepta(null); }}
                error={errorAcepta}
                disabled={status.loading}
            />

            <div className="mt-8 flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
                {/* El widget mide 304 px fijos: en pantallas muy angostas se recorta en vez de desbordar */}
                <div className="max-w-full overflow-hidden">
                    <ReCAPTCHA
                        ref={refCaptcha}
                        sitekey="6Le4g90qAAAAAPEllrP6xhYjzC3OymmuyiB4TO0F"
                        theme="dark"
                        onChange={(e) => setStatusCaptcha(e)}
                    />
                </div>
                <PillLink type="submit" disabled={status.loading}>
                    {status.loading ? textos.enviando : textos.boton}
                </PillLink>
            </div>

            {status.error && (
                <p className="mt-6 text-sm text-red-300" role="alert">
                    {status.error}
                </p>
            )}
            {status.success && (
                <Exito className="mt-6 text-sm text-cream" role="status">
                    {textos.exito}
                </Exito>
            )}
        </form>
    );
}

export default Formulario;